import React from "react";
import { useHistory } from "react-router-dom";
import {
  WSActions,
  WSButton,
  WSCard,
  WSControl,
  WSGrid,
  WSInfoBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../../query/WSQuery";
import { useFlowContext } from "../../../../shared/components/Flow";
import { DataItem } from "../../../../shared/components/DataItem";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { PATH_SETTINGS_ACCOUNT_INFO } from "../../../Settings/paths";
import { EnrollMeridioFlowState } from "./types";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const StepConfirmInfo: React.FC<FlowStep> = props => {
  const history = useHistory();
  const userId = useUserId();
  const queryMemberProfile = useQueryMemberProfile(userId);
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();

  const canContinue =
    flowState.tobaccoUse !== undefined && flowState.ssnConsent === true;

  const handleContinue = () => {
    props.onNext?.();
  };

  return (
    <>
      <WSText.Heading3 weight="medium" mb="S">
        Confirm your information
      </WSText.Heading3>
      <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
        Confirm that your information is correct and up to date.
      </WSText.ParagraphSm>

      <WSList gap="L">
        <WSCard>
          <WSInfoBox transparent icon="help-circle">
            The following information is collected from your Wingspan profile.
            To update your information, go to{" "}
            <WSButton.Link
              onClick={() => history.push(PATH_SETTINGS_ACCOUNT_INFO)}
            >
              Account settings.
            </WSButton.Link>
          </WSInfoBox>
        </WSCard>

        <WSCard>
          <WSText weight="medium" mb="XL">
            Individual (you)
          </WSText>
          <WSQueries queries={{ queryMemberProfile }}>
            {({ queryMemberProfileData }) => {
              const profile = queryMemberProfileData.user.profile;
              const dataItems = [
                {
                  label: "Legal first name",
                  value: profile.firstName ?? "—"
                },
                { label: "Legal last name", value: profile.lastName ?? "—" },
                {
                  label: "Date of birth",
                  value: profile.dob
                    ? toWSDateString(
                        new Date(profile.dob as any),
                        "monthDayYear"
                      )
                    : "—"
                },
                { label: "Gender", value: (profile as any).gender ?? "—" }
              ];
              return (
                <WSList gap="XL">
                  <WSGrid gutter="2XL">
                    {dataItems.map(item => (
                      <WSGrid.Item key={item.label} span={{ xs: "12", s: "6" }}>
                        <DataItem label={item.label} value={item.value} />
                      </WSGrid.Item>
                    ))}
                  </WSGrid>

                  <WSList gap="L" mb="M">
                    <WSText.ParagraphSm color="gray500" weight="book">
                      Do you use tobacco? *
                    </WSText.ParagraphSm>
                    <WSControl
                      type="radio"
                      value={flowState.tobaccoUse === true}
                      onChange={() =>
                        setFlowState(prev => ({ ...prev, tobaccoUse: true }))
                      }
                      label="Yes"
                    />
                    <WSControl
                      type="radio"
                      value={flowState.tobaccoUse === false}
                      onChange={() =>
                        setFlowState(prev => ({ ...prev, tobaccoUse: false }))
                      }
                      label="No"
                    />
                  </WSList>
                  <WSInfoBox>
                    <WSText.ParagraphSm weight="book" color="gray700">
                      Your SSN, previously provided for tax purposes, will also
                      be shared with Meridio to process your health insurance
                      enrollment.
                    </WSText.ParagraphSm>
                    <WSControl
                      mt="L"
                      type="checkbox"
                      value={flowState.ssnConsent === true}
                      onChange={checked =>
                        setFlowState(prev => ({ ...prev, ssnConsent: checked }))
                      }
                      label="I agree to my SSN being shared with Meridio for enrollment processing."
                      size="S"
                    />
                  </WSInfoBox>
                </WSList>
              );
            }}
          </WSQueries>
        </WSCard>
      </WSList>

      <WSActions
        mt="XL"
        alignment="fill"
        buttons={[
          { kind: "Tertiary", label: "Back", onClick: props.onBack },
          {
            label: "Continue",
            onClick: handleContinue,
            disabled: !canContinue
          }
        ]}
      />
    </>
  );
};
